'use client';
import { useEffect, useMemo } from 'react';
import {
  PlaywireAdPlacement,
  playwireAdPlacementSizeKey,
  StandardAdPlacement,
} from '@/features/ads/shared/types/playwire-ad-placement';
import { playwireEnabled } from '@/features/ads/shared/helper/playwire-enabled';
import {
  queueDestroyAllRampUnits,
  queueRampCommand,
  RampAdUnit,
} from '@/features/ads/shared/helper/playwire-runtime';
import { useIsLg, useIsMd } from '@/shared/hooks/use-breakpoints';
import { usePathname } from 'next/navigation';

type Props = {
  placements?: PlaywireAdPlacement[];
  placementsMd?: PlaywireAdPlacement[];
  placementsLg?: PlaywireAdPlacement[];
  category?: string;
  sku?: string;
  gameType?: string;
};

export function isStandardAdPlacement(adPlacement: {
  type: string;
}): adPlacement is StandardAdPlacement {
  return (Object.values(playwireAdPlacementSizeKey) as string[]).includes(adPlacement.type);
}

function playwireAdPlacementToRampAdUnit(adPlacements: PlaywireAdPlacement[]): RampAdUnit[] {
  return adPlacements.map((placement) => {
    if (isStandardAdPlacement(placement)) {
      return {
        type: placement.type,
        ...(placement?.code ? { selectorId: placement.code } : {}),
      };
    } else {
      return {
        type: placement.type,
      };
    }
  });
}

export const PlaywireAdsInitializer = ({
  placements,
  placementsMd,
  placementsLg,
  sku,
  gameType,
  category,
}: Props) => {
  const playwireActive = playwireEnabled();
  const isMd = useIsMd();
  const isLg = useIsLg();
  const pathname = usePathname();

  // This is needed for a first A/B Test with playwire in that specific page
  const testPath = '/p/cards/solitaire-with-themes/play';
  const addPlayCustomPath = useMemo(() => !!pathname && pathname == testPath, [pathname]);

  const effectivePlacements = useMemo<PlaywireAdPlacement[] | undefined>(() => {
    if (isLg && placementsLg?.length) return placementsLg;
    if (isMd && placementsMd?.length) return placementsMd;

    return placements ?? placementsMd ?? placementsLg;
  }, [isMd, isLg, placements, placementsMd, placementsLg]);

  useEffect(() => {
    if (!playwireActive) return;
    let ads: RampAdUnit[] = [];
    if (effectivePlacements && effectivePlacements?.length !== 0) {
      ads = playwireAdPlacementToRampAdUnit(effectivePlacements);
    }
    queueRampCommand((ramp) => {
      window.PageOS.setClientTag(1, category || '');
      window.PageOS.setClientTag(2, gameType || '');
      window.PageOS.setClientTag(3, sku || '');

      if (typeof ramp.spaAds !== 'function') {
        return;
      }

      if (addPlayCustomPath) {
        ramp.spaAds({
          ads,
          countPageview: true,
          path: '/play',
        });
        return;
      }

      ramp.spaAds({
        ads,
        countPageview: true,
      });
    });
  }, [category, effectivePlacements, gameType, playwireActive, sku, addPlayCustomPath]);

  // Only pages with ad slots render this initializer, so the next page may never call spaAds:
  // drop this page's units (sticky ones included) when leaving it.
  useEffect(() => {
    if (!playwireActive) return;
    return queueDestroyAllRampUnits;
  }, [playwireActive]);

  return null;
};
